import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Footer } from '../common';
import HomeNavBar from '../HomePage/HomeNavBar';
import BannerUpload from './BannerUpload';
import ProfileIdentity from './ProfileIdentity';
import BioEditor from './BioEditor';
import ConnectedPlatforms from './ConnectedPlatforms';
import IntroVideo from './IntroVideo';
import FollowersPopup from './FollowersPopup';
import MediaGallery from './MediaGallery';
import LiveStreamBlock from './LiveStreamBlock';
import { ROUTES } from '../../constants';
import {
  FOLLOW_GRAPH_UPDATED_EVENT,
  authApi,
  clearStoredAuth,
  readStoredToken,
} from '../../services/authApi';
import streamApi from '../../services/streamApi';
import { readStoredUser, syncStoredUser, useNavbarUser } from '../../utils/navbarUser';
import { hasDirectAdminAccess } from '../../utils/adminAccess';
import './ProfilePage.css';

function isBlobUrl(value) {
  return typeof value === 'string' && value.startsWith('blob:');
}

function revokeObjectUrl(value) {
  if (isBlobUrl(value)) {
    URL.revokeObjectURL(value);
  }
}

function buildProfileState(user) {
  const profileImage =
    user?.profileImage && !user.profileImage.startsWith('blob:')
      ? user.profileImage
      : null;

  return {
    id: user?.id || null,
    bannerUrl: null,
    avatarUrl: profileImage,
    username: user?.username || user?.firstName || 'StreamerPro',
    bio: '',
    connectedAccounts: [],
    videoUrl: null,
    followersCount: 0,
    followingCount: 0,
    mediaVisibility: 'followers',
  };
}

function formatJoinedDate(createdAt) {
  if (!createdAt) return 'today';

  const parsed = new Date(createdAt);
  if (Number.isNaN(parsed.getTime())) return 'today';

  return parsed.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function mapConnectedAccounts(connectedPlatforms = []) {
  return (connectedPlatforms || []).map((platform) => ({
    platformId: platform.platform,
    username: platform.platformUsername,
    profileUrl: platform.profileUrl || null,
  }));
}

/**
 * ProfilePage — User profile setup and customization.
 *
 * A premium onboarding experience featuring:
 * - Top navigation with logo and logout
 * - Banner upload with drag-and-drop
 * - Avatar with circular crop
 * - Bio editor with character counter
 * - Connected platforms management
 * - Intro video upload
 * - Continue button for completion
 * - Footer with legal links
 *
 * @component
 * @returns {JSX.Element}
 */
function ProfilePage() {
  const navigate = useNavigate();
  const storedUser = readStoredUser();
  const user = useNavbarUser();

  /* ──────────────────────────────────────────────────────────────────────────
   * Profile State
   * ────────────────────────────────────────────────────────────────────────── */
  const [profile, setProfile] = useState(() => buildProfileState(storedUser));
  const [joinedDate, setJoinedDate] = useState(() => formatJoinedDate(storedUser?.createdAt));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [logoRoute] = useState(() => (hasDirectAdminAccess() ? ROUTES.ADMIN : ROUTES.HOME));
  const [uploadingMedia, setUploadingMedia] = useState({
    avatar: false,
    banner: false,
    introVideo: false,
  });
  const [showFollowersPopup, setShowFollowersPopup] = useState(false);
  const [followersPopupTab, setFollowersPopupTab] = useState('followers');
  const [liveStatus, setLiveStatus] = useState(null);

  const setMediaUploading = useCallback((kind, value) => {
    setUploadingMedia((prev) => ({ ...prev, [kind]: value }));
  }, []);

  useEffect(() => {
    const token = readStoredToken();
    if (!token) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const loadProfile = async () => {
      try {
        const [profileResponse, introResponse] = await Promise.all([
          authApi.getProfile(token),
          authApi.getIntroVideo(token).catch(() => ({ introVideo: null })),
        ]);

        if (cancelled) return;

        const profileData = profileResponse?.profile || {};
        const connectedAccounts = mapConnectedAccounts(profileData.connectedPlatforms);
        const persistedBannerUrl =
          profileData.bannerUrl && !profileData.bannerUrl.startsWith('blob:')
            ? profileData.bannerUrl
            : null;
        const persistedProfileImage =
          profileData.profilePictureUrl && !profileData.profilePictureUrl.startsWith('blob:')
            ? profileData.profilePictureUrl
            : null;
        const storedProfileImage =
          storedUser?.profileImage && !storedUser.profileImage.startsWith('blob:')
            ? storedUser.profileImage
            : null;
        const nextAvatar =
          persistedProfileImage ||
          profileData.oauthProfileImage ||
          storedProfileImage ||
          null;
        const publicProfileResponse = profileData.username
          ? await authApi.getPublicProfile(profileData.username).catch(() => ({ profile: null }))
          : { profile: null };

        setProfile((prev) => ({
          ...prev,
          id: profileData.id || prev.id,
          bannerUrl: persistedBannerUrl,
          avatarUrl: nextAvatar,
          username: profileData.username || profileData.firstName || prev.username,
          bio: profileData.bio || '',
          connectedAccounts,
          videoUrl: introResponse?.introVideo?.s3Link || null,
          followersCount: publicProfileResponse?.profile?.followersCount || 0,
          followingCount: publicProfileResponse?.profile?.followingCount || 0,
          mediaVisibility: profileData.mediaVisibility || 'followers',
          oauthProvider: profileData.oauthProvider || null,
          oauthProviderUsername: profileData.oauthProviderUsername || null,
        }));
        setJoinedDate(profileData.joinedDate || formatJoinedDate(storedUser?.createdAt));
        syncStoredUser({
          username: profileData.username || storedUser?.username,
          firstName: profileData.firstName || storedUser?.firstName,
          lastName: profileData.lastName || storedUser?.lastName,
          profileImage: nextAvatar,
          createdAt: storedUser?.createdAt,
        });
      } catch (error) {
        console.error('Failed to load profile data:', error);
        if (!cancelled) {
          setError(error.message || 'Failed to load profile');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadProfile();

    return () => {
      cancelled = true;
    };
  }, [navigate, storedUser?.createdAt, storedUser?.firstName, storedUser?.lastName, storedUser?.profileImage, storedUser?.username]);

  useEffect(() => {
    const currentUsername = (profile.username || '').trim();
    if (!currentUsername) {
      return undefined;
    }

    let cancelled = false;

    const refreshSocialCounts = async () => {
      try {
        const publicProfileResponse = await authApi.getPublicProfile(currentUsername);
        if (cancelled) return;

        setProfile((prev) => ({
          ...prev,
          followersCount: publicProfileResponse?.profile?.followersCount || 0,
          followingCount: publicProfileResponse?.profile?.followingCount || 0,
        }));
      } catch (countsError) {
        if (!cancelled) {
          console.error('Failed to refresh social counts:', countsError);
        }
      }
    };

    const handleFollowGraphUpdated = () => {
      refreshSocialCounts();
    };

    window.addEventListener(FOLLOW_GRAPH_UPDATED_EVENT, handleFollowGraphUpdated);

    return () => {
      cancelled = true;
      window.removeEventListener(FOLLOW_GRAPH_UPDATED_EVENT, handleFollowGraphUpdated);
    };
  }, [profile.username]);

  useEffect(() => {
    const profileId = profile?.id;
    if (!profileId) {
      setLiveStatus(null);
      return undefined;
    }

    let cancelled = false;

    const fetchLive = async () => {
      try {
        const statuses = await streamApi.getLiveStatuses([profileId]);
        if (cancelled) return;
        const entry = Array.isArray(statuses)
          ? statuses.find((s) => String(s.user_id) === String(profileId))
          : null;
        setLiveStatus(entry && entry.is_live ? entry : null);
      } catch {
        if (!cancelled) setLiveStatus(null);
      }
    };

    fetchLive();
    const interval = setInterval(fetchLive, 30000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [profile.id]);

  /* ──────────────────────────────────────────────────────────────────────────
   * Handlers
   * ────────────────────────────────────────────────────────────────────────── */

  const handleBannerChange = useCallback(async (url) => {
    const previousUrl = profile.bannerUrl;
    setProfile((prev) => ({ ...prev, bannerUrl: url }));
    try {
      await authApi.updateProfile({ bannerUrl: url });
    } catch (saveError) {
      console.error('Failed to save banner:', saveError);
      setProfile((prev) => ({ ...prev, bannerUrl: previousUrl }));
    }
  }, [profile.bannerUrl]);

  const handleBannerFile = useCallback(async (file) => {
    const previousUrl = profile.bannerUrl;
    const previewUrl = URL.createObjectURL(file);

    setMediaUploading('banner', true);
    setError(null);
    setProfile((prev) => ({ ...prev, bannerUrl: previewUrl }));

    try {
      const uploadData = await authApi.uploadProfileMedia({ kind: 'banner', file });
      await authApi.updateProfile({ bannerUrl: uploadData.fileUrl });
      revokeObjectUrl(previewUrl);
      setProfile((prev) => ({ ...prev, bannerUrl: uploadData.fileUrl }));
    } catch (saveError) {
      console.error('Failed to upload banner:', saveError);
      revokeObjectUrl(previewUrl);
      setProfile((prev) => ({ ...prev, bannerUrl: previousUrl }));
      setError(saveError.message || 'Failed to upload banner');
    } finally {
      setMediaUploading('banner', false);
    }
  }, [profile.bannerUrl, setMediaUploading]);

  const handleAvatarFile = useCallback(async (file) => {
    const previousUrl = profile.avatarUrl;
    const previewUrl = URL.createObjectURL(file);

    setMediaUploading('avatar', true);
    setError(null);
    setProfile((prev) => ({ ...prev, avatarUrl: previewUrl }));

    try {
      const uploadData = await authApi.uploadProfileMedia({ kind: 'avatar', file });
      await authApi.updateProfile({ profilePictureUrl: uploadData.fileUrl });
      revokeObjectUrl(previewUrl);
      setProfile((prev) => ({ ...prev, avatarUrl: uploadData.fileUrl }));
      syncStoredUser({ profileImage: uploadData.fileUrl });
    } catch (saveError) {
      console.error('Failed to upload avatar:', saveError);
      revokeObjectUrl(previewUrl);
      setProfile((prev) => ({ ...prev, avatarUrl: previousUrl }));
      setError(saveError.message || 'Failed to upload avatar');
    } finally {
      setMediaUploading('avatar', false);
    }
  }, [profile.avatarUrl, setMediaUploading]);

  const handleBioChange = useCallback(async (bio) => {
    setProfile((prev) => ({ ...prev, bio }));
    try {
      await authApi.updateProfile({ bio });
    } catch (saveError) {
      console.error('Failed to save bio:', saveError);
    }
  }, []);

  const handleMediaVisibilityChange = useCallback(async (nextValue) => {
    if (nextValue !== 'followers' && nextValue !== 'private') return;
    const previousValue = profile.mediaVisibility;
    setProfile((prev) => ({ ...prev, mediaVisibility: nextValue }));
    try {
      await authApi.updateProfile({ mediaVisibility: nextValue });
    } catch (saveError) {
      console.error('Failed to save media visibility:', saveError);
      setProfile((prev) => ({ ...prev, mediaVisibility: previousValue }));
    }
  }, [profile.mediaVisibility]);

  const handleAddAccount = useCallback(async ({ platformId, username }) => {
    try {
      const data = await authApi.addPlatform({
        platform: platformId,
        platformUsername: username,
      });
      const connectedAccounts = mapConnectedAccounts(data.profile?.connectedPlatforms);
      setProfile((prev) => ({ ...prev, connectedAccounts }));
    } catch (saveError) {
      console.error('Failed to add platform:', saveError);
    }
  }, []);

  const handleRemoveAccount = useCallback(async (platformId) => {
    try {
      const data = await authApi.removePlatform(platformId);
      const connectedAccounts = mapConnectedAccounts(data.profile?.connectedPlatforms);
      setProfile((prev) => ({ ...prev, connectedAccounts }));
    } catch (saveError) {
      console.error('Failed to remove platform:', saveError);
    }
  }, []);

  const handleVideoChange = useCallback((url) => {
    setProfile((prev) => ({ ...prev, videoUrl: url }));

    if (url !== null) {
      return;
    }

    authApi.updateIntroVideo({
      s3Link: null,
      processingStatus: 'removed',
    }).catch((saveError) => {
      console.error('Failed to remove intro video:', saveError);
      setError(saveError.message || 'Failed to remove hero video');
    });
  }, []);

  const handleVideoFile = useCallback(async (file, previewUrl) => {
    const previousUrl = profile.videoUrl;

    setMediaUploading('introVideo', true);
    setError(null);

    try {
      const uploadData = await authApi.uploadProfileMedia({ kind: 'intro-video', file });
      await authApi.updateIntroVideo({
        s3Link: uploadData.fileUrl,
        processingStatus: 'uploaded',
      });
      revokeObjectUrl(previewUrl);
      setProfile((prev) => ({ ...prev, videoUrl: uploadData.fileUrl }));
    } catch (saveError) {
      console.error('Failed to upload intro video:', saveError);
      revokeObjectUrl(previewUrl);
      setProfile((prev) => ({ ...prev, videoUrl: previousUrl }));
      setError(saveError.message || 'Failed to upload intro video');
    } finally {
      setMediaUploading('introVideo', false);
    }
  }, [profile.videoUrl, setMediaUploading]);

  const handleLogout = useCallback(async () => {
    await authApi.logout();
    clearStoredAuth();
    setProfile({
      bannerUrl: null,
      avatarUrl: null,
      username: 'StreamerPro',
      bio: '',
      connectedAccounts: [],
      videoUrl: null,
      followersCount: 0,
      followingCount: 0,
    });
    navigate(ROUTES.WELCOME);
  }, [navigate]);

  const handleContinue = useCallback(async () => {
    setSaving(true);
    try {
      await authApi.updateProfile({
        bio: profile.bio,
        profilePictureUrl: profile.avatarUrl,
        bannerUrl: profile.bannerUrl,
      });
      navigate(ROUTES.HOME);
    } catch (saveError) {
      console.error('Failed to save profile on continue:', saveError);
      navigate(ROUTES.HOME);
    } finally {
      setSaving(false);
    }
  }, [navigate, profile.avatarUrl, profile.bannerUrl, profile.bio]);

  if (loading) {
    return (
      <div className="profile-page profile-page--loading">
        <p style={{ color: '#7a8fb5', textAlign: 'center', paddingTop: '20vh' }}>
          Loading profile…
        </p>
      </div>
    );
  }

  return (
    <div className="profile-page">
      {/* Navigation — minimal mode hides search and filter */}
      <HomeNavBar
        spadesBalance={user.spades}
        notificationCount={user.notifications}
        userAvatar={profile.avatarUrl}
        userName={profile.username}
        onSpadesClick={() => {}}
        onLogout={handleLogout}
        logoRoute={logoRoute}
        minimal
      />

      {/* Main Content */}
      <main className="profile-page__content">
        {error && (
          <div style={{ color: '#ef4444', textAlign: 'center', padding: '12px', fontSize: '14px' }}>
            {error}
          </div>
        )}

        {liveStatus && (
          <LiveStreamBlock
            profile={{
              id: profile.id,
              username: profile.username,
              oauthProvider: profile.oauthProvider,
              oauthProviderUsername: profile.oauthProviderUsername,
              connectedPlatforms: (profile.connectedAccounts || []).map((a) => ({
                platform: a.platformId,
                platformUsername: a.username,
              })),
            }}
            liveStatus={liveStatus}
          />
        )}

        {/* Banner Section */}
        <section className="profile-page__section profile-page__section--banner">
          <BannerUpload
            bannerUrl={profile.bannerUrl}
            onBannerChange={handleBannerChange}
            onBannerFile={handleBannerFile}
            uploading={uploadingMedia.banner}
          />
        </section>

        {/* Identity Section */}
        <section className="profile-page__section profile-page__section--identity">
          <ProfileIdentity
            avatarUrl={profile.avatarUrl}
            onAvatarFile={handleAvatarFile}
            username={profile.username}
            joinedDate={joinedDate}
            followers={profile.followersCount || 0}
            following={profile.followingCount || 0}
            uploading={uploadingMedia.avatar}
            onFollowersClick={profile.username ? () => {
              setFollowersPopupTab('followers');
              setShowFollowersPopup(true);
            } : null}
            onFollowingClick={profile.username ? () => {
              setFollowersPopupTab('following');
              setShowFollowersPopup(true);
            } : null}
          />
        </section>

        {/* Bio Section */}
        <section className="profile-page__section profile-page__section--bio">
          <BioEditor value={profile.bio} onChange={handleBioChange} />
        </section>

        {/* Connected Platforms Section */}
        <section className="profile-page__section profile-page__section--platforms">
          <ConnectedPlatforms
            accounts={profile.connectedAccounts}
            onAddAccount={handleAddAccount}
            onRemoveAccount={handleRemoveAccount}
            onAccountsUpdate={async () => {
              try {
                const data = await authApi.getProfile();
                const profileData = data?.profile || {};
                setProfile((prev) => ({
                  ...prev,
                  connectedAccounts: mapConnectedAccounts(profileData.connectedPlatforms),
                  oauthProvider: profileData.oauthProvider || prev.oauthProvider,
                  oauthProviderUsername: profileData.oauthProviderUsername || prev.oauthProviderUsername,
                }));
              } catch (refreshError) {
                console.error('Failed to refresh connected platforms:', refreshError);
              }
            }}
          />
        </section>

        {/* Intro Video Section */}
        <section className="profile-page__section profile-page__section--video">
          <IntroVideo
            videoUrl={profile.videoUrl}
            onVideoChange={handleVideoChange}
            onVideoFile={handleVideoFile}
            uploading={uploadingMedia.introVideo}
          />
        </section>

        {/* Media Visibility Toggle */}
        <section className="profile-page__section profile-page__section--media-visibility">
          <div className="profile-media-visibility">
            <div className="profile-media-visibility__copy">
              <h3 className="profile-media-visibility__title">Media visibility</h3>
              <p className="profile-media-visibility__hint">
                {profile.mediaVisibility === 'followers'
                  ? 'Your approved media is visible to your followers.'
                  : 'Your approved media is visible only to you.'}
              </p>
            </div>
            <label className="profile-media-visibility__switch">
              <input
                type="checkbox"
                checked={profile.mediaVisibility === 'followers'}
                onChange={(event) => handleMediaVisibilityChange(
                  event.target.checked ? 'followers' : 'private',
                )}
              />
              <span>{profile.mediaVisibility === 'followers' ? 'Followers' : 'Private'}</span>
            </label>
          </div>
        </section>

        {/* Own Media Gallery */}
        <section className="profile-page__section profile-page__section--media-gallery">
          <MediaGallery userId={profile.id} isOwnProfile />
        </section>

        {/* Continue Button */}
        <div className="profile-page__continue-section">
          <button
            type="button"
            className="profile-page__continue"
            onClick={handleContinue}
            disabled={saving}
          >
            <span>{saving ? 'Saving…' : 'Continue'}</span>
            {!saving && <span className="profile-page__continue-arrow">→</span>}
          </button>
        </div>
      </main>

      <FollowersPopup
        isOpen={showFollowersPopup}
        onClose={() => setShowFollowersPopup(false)}
        username={profile.username}
        initialTab={followersPopupTab}
      />

      {/* Footer */}
      <Footer fullWidth />
    </div>
  );
}

export default ProfilePage;
